import { useContext, useState, useEffect, memo } from 'react';
import Path from 'path-to-regexp';
import { Link } from 'react-router-dom';
import { Paperclip, ChevronDown } from 'lucide-react';

import { ROUTE_INSPECT_TRACE_LINK } from '@/constant/routes';
import { TraceInspectorContext } from '@/components/traceInspector/traceInspector.context';
import { SearchContext } from '@/components/ui/search/searchContext';
import { RichEditor } from '@/components/ui/richEditor';
import { getByPath } from '@/utils/widgets';
import { AnswerCard } from '@/components/ui/answerCard/answerCard';
import {
  formatDateWithoutSeconds,
  isJSON,
  checkCommentExists,
  pluralize
} from '@/utils';
import { PATHS } from './constants.comment';
import { AvatarComponent } from '@/components/ui/avatar/avatar';
import { Badge } from '@/shadcn/badge';
import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent
} from '@/shadcn/tooltip';
import { WidgetData } from '@/components/ui/widget/widget.type';
import { CommentView } from './comment.view.type';
import { TextView } from '@/components/ui/widget/views/text/text.view.type';

type Props = {
  view: CommentView;
  data: WidgetData;
};

export const CommentViewImpl = memo(({ view, data }: Props) => {
  const traceInspectorContext = useContext(TraceInspectorContext);
  const { traceId, currentHash } = traceInspectorContext || {};
  const { state: searchState } = useContext(SearchContext) || {};
  const {
    namePath = PATHS.NAME,
    avatarPath = PATHS.AVATAR,
    groupPath = PATHS.GROUP,
    datePath = PATHS.DATE,
    actionPath = PATHS.ACTION,
    commentPath = PATHS.COMMENT,
    linkHashPath = PATHS.HASH,
    heightPath = PATHS.HEIGHT,
    filesPath = PATHS.FILES,
    answersPath = PATHS.ANSWERS,
    path
  } = view;

  const name = getByPath(data, namePath);
  const avatar = getByPath(data, avatarPath);
  const group = getByPath(data, groupPath);
  const date = getByPath(data, datePath);
  const actionName = getByPath(data, actionPath);
  const hash = getByPath(data, linkHashPath);
  const linkHeight = getByPath(data, heightPath);
  const files = getByPath(data, filesPath);
  const commentPathValue = commentPath || path;
  const comment = getByPath(data, commentPathValue);
  const answers = getByPath(data, answersPath);

  const isCommentWithSubject = () => {
    const commentArray = isJSON(comment) ? JSON.parse(comment) : [];
    return commentArray.some(c => c?.type === 'subject');
  };
  const [showAnswers, setShowAnswers] = useState(true);

  useEffect(() => {
    const hasMatchingAnswer = searchState?.string
      ? answers?.some(answer =>
          answer.data.answer.includes(searchState?.string)
        )
      : false;
    setShowAnswers(hasMatchingAnswer);
  }, [searchState?.string, answers]);

  const isCurrentLink = currentHash === hash;

  if (!data || !checkCommentExists(data[commentPath])) {
    return null;
  }

  return (
    <section className="text-card-foreground flex pb-2">
      <AvatarComponent source={avatar} name={name} />
      <div className="mx-[13px] min-h-[70px] w-full">
        <ul>
          {data?.user && (
            <li className="flex flex-row flex-wrap items-center">
              <div className="text-md mr-2 font-semibold">{name}</div>
              {group && (
                <div className="border-input text-card-foreground/80 border-l pl-2.5 text-xs">
                  {group}
                </div>
              )}
            </li>
          )}
          {date && (
            <li>
              {traceId && hash ? (
                <div className="flex flex-row items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Link
                          to={Path.compile(ROUTE_INSPECT_TRACE_LINK)({
                            id: traceId,
                            linkid: hash
                          })}
                          className={cn(
                            isCurrentLink &&
                              'pointer-events-none cursor-not-allowed'
                          )}
                          aria-disabled={isCurrentLink}
                          aria-label={`Link to the ${actionName} action.`}
                        >
                          <Badge className="hover:border-primary-deep/40 h-5">
                            {linkHeight} - {actionName}
                          </Badge>
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent>
                        {isCurrentLink ? (
                          <p>
                            This link is disabled because you are already on the
                            action.
                          </p>
                        ) : (
                          <p>
                            Click to open the{' '}
                            <b className="font-bold">{actionName}</b> action.
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <span className="text-muted-foreground py-0.5 text-[10px]">
                    Commented on {formatDateWithoutSeconds(date)}
                  </span>
                </div>
              ) : (
                <div>
                  <span className="text-muted-foreground py-0.5 text-[10px]">
                    Commented on {formatDateWithoutSeconds(date)}
                  </span>
                </div>
              )}
            </li>
          )}
          <li>
            <div>
              <RichEditor
                value={comment}
                withSubject={isCommentWithSubject()}
                readOnly
                searchValue={searchState?.string}
              />
            </div>
          </li>
          {files?.length > 0 && (
            <Link
              to={Path.compile(ROUTE_INSPECT_TRACE_LINK)({
                id: traceId,
                linkid: hash
              })}
              className={cn(
                'text-muted-foreground hover:text-primary-deep mt-2 flex flex-row items-center gap-2 text-xs hover:underline',
                isCurrentLink && 'cursor-not-allowed'
              )}
              aria-disabled={isCurrentLink}
              aria-label={`Link to the ${actionName} action.`}
            >
              <Paperclip size={14} />
              <i>{pluralize(data?.files?.length, 'Attachment')}</i>
            </Link>
          )}
          {answers?.length > 0 && (
            <button
              className="text-muted-foreground flex items-center text-xs font-normal underline"
              onClick={() => setShowAnswers(!showAnswers)}
            >
              {pluralize(answers?.length, 'Answer')}
              <div
                className={cn(
                  'ml-1 transition-transform',
                  showAnswers && 'rotate-180'
                )}
              >
                <ChevronDown size={12} />
              </div>
            </button>
          )}
          {showAnswers &&
            answers?.length > 0 &&
            answers
              ?.sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
              .map((answer, i) => (
                <AnswerCard
                  data={answer}
                  key={i}
                  searchValue={searchState?.string}
                />
              ))}
        </ul>
      </div>
    </section>
  );
});

/** Sorts defaults to text on `datePath` (text is ok assuming ISO dates) */
const getSortConfig = (view: CommentView): TextView => ({
  type: 'text',
  path: view.datePath || PATHS.DATE
});

/** Filters defaults to text search on comment content */
const getFilterConfig = (view: CommentView): TextView => ({
  type: 'text',
  path: view.commentPath || PATHS.COMMENT
});

/** Gets the default width which is `'large'` */
const getDefaultWidth = (): 'large' => 'large';

/** Stringifies data at commentPath */
const getStringifyFunction = (view: CommentView) => {
  const { commentPath = PATHS.COMMENT } = view;
  return (data: WidgetData): unknown => getByPath(data, commentPath) || '';
};

export const commentViewProvider = {
  component: CommentViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
